/**
 * The two files behind the Payroll hours page.
 *
 * Download for payroll: the file a person reads. Five columns (Name, Date,
 * Hours, Rate, Client or internal), the header then one row per line paid
 * in this period, people in order and each person's lines together, and
 * nothing else: no total row and no empty row (owner ruling, LP-39: a
 * parser would read a total row as more hours). Paid in this period means
 * in_period true and not a per-visit time-only line. The totals are hours
 * worked: every paid line but a SPICE Travel line (kind travel, one unit of
 * a pay code, LP-46), exactly the lines the server's summary counts, so a
 * person's lines other than SPICE Travel sum to the page's total for them.
 * (A demo client's note never reaches the wire.) The page's preview
 * renders the same payrollFilePeople, with each person's total on screen
 * only, so preview and file cannot disagree.
 *
 * Download full detail: every ledger line the response carries, in the
 * twenty columns the Payroll Project's overtime and retro rules read. A
 * reader leaves a SPICE Travel line out of hours worked by its kind
 * (travel) or its rate_band (spice:travel); derived_from names its note.
 *
 * Hours are written verbatim from the wire and totals are summed in
 * integer hundredths, never as floats. RFC 4180 with CRLF line ends,
 * through the same buildCsv the billing file uses. Pure: no DOM, no
 * clock, no rate, no amount.
 *
 * Run-time imports are relative and leaf-only, so the server's
 * integration tests can import this module (no @/ alias resolves there).
 */

import { buildCsv } from '../../lib/csv';
import { sumHours } from '../../lib/hours';
import { TIME_ENTRY_KIND_LABEL } from '../../lib/timeEntryKinds';
import type { PayrollHoursResponse, PayrollHoursRowWire, PayrollPeriodWire } from '@/lib/payrollApi';

export const PAYROLL_CSV_HEADER = ['Name', 'Date', 'Hours', 'Rate', 'Client or internal'] as const;

/** One comparison of text for every sort here. */
export const PAYROLL_COLLATOR = new Intl.Collator('en');

const PER_VISIT_TIME_KIND = 'per_visit_time';
/** A SPICE Travel line's kind (api/_shared/payrollPerVisit.ts
 *  SPICE_TRAVEL_KIND): one unit of a pay code, paid and printed, never
 *  counted as hours worked. */
export const SPICE_TRAVEL_KIND = 'travel';

export interface PayrollFileLine {
  name: string;
  date: string;
  hours: string;
  rate: string;
  clientOrInternal: string;
  /** A SPICE Travel line: a pay unit the totals leave out. */
  travel: boolean;
}

export interface PayrollFilePerson {
  staffUserId: string;
  name: string;
  lines: PayrollFileLine[];
  /** Hours worked: every line but the SPICE Travel lines. */
  total: string;
  /** How many SPICE Travel lines the person has (long-distance visits). */
  travelVisits: number;
}

/** Paid in this period: in the period (strictly true) and not a
 *  per-visit time-only line. The server's summary counts exactly these. */
export function isPaidPayrollLine(row: PayrollHoursRowWire): boolean {
  return row.in_period === true && row.kind !== PER_VISIT_TIME_KIND;
}

/** An internal kind's word; a kind with no word prints as itself. */
function kindLabel(kind: string): string {
  return Object.hasOwn(TIME_ENTRY_KIND_LABEL, kind) ? TIME_ENTRY_KIND_LABEL[kind as keyof typeof TIME_ENTRY_KIND_LABEL] : kind;
}

/** The roster's full name, or the login's display name when there is none. */
function printedName(row: PayrollHoursRowWire): string {
  return row.roster_full_name.trim() !== '' ? row.roster_full_name : row.staff_name;
}

/** The kind of work: Internal for internal time; for a case-note line the
 *  band's label (After Hours appended when the line is flagged), or the
 *  internal kind a per-visit pay line names. */
export function payrollFileRate(row: PayrollHoursRowWire): string {
  if (row.source === 'internal') return 'Internal';
  let label = '';
  if (row.rate_band !== '') label = row.rate_label;
  else if (row.derived_from !== '') label = kindLabel(row.kind);
  if (!row.after_hours) return label;
  return label === '' ? 'After Hours' : `${label} After Hours`;
}

function fileLine(row: PayrollHoursRowWire): PayrollFileLine {
  return {
    name: printedName(row),
    date: row.work_date,
    hours: row.hours,
    rate: payrollFileRate(row),
    clientOrInternal: row.source === 'internal' ? kindLabel(row.kind) : row.client,
    travel: row.kind === SPICE_TRAVEL_KIND,
  };
}

function byLine(a: PayrollFileLine, b: PayrollFileLine): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  return PAYROLL_COLLATOR.compare(a.clientOrInternal, b.clientOrInternal) || PAYROLL_COLLATOR.compare(a.rate, b.rate);
}

/** The file's people in order, each with their lines in order and their total. */
export function payrollFilePeople(response: PayrollHoursResponse): PayrollFilePerson[] {
  const people = new Map<string, PayrollFilePerson>();
  for (const row of response.rows) {
    if (!isPaidPayrollLine(row)) continue;
    const line = fileLine(row);
    let person = people.get(row.staff_user_id);
    if (person === undefined) {
      person = { staffUserId: row.staff_user_id, name: line.name, lines: [], total: '0.00', travelVisits: 0 };
      people.set(row.staff_user_id, person);
    }
    person.lines.push(line);
  }
  const out = [...people.values()];
  for (const person of out) {
    // Array.prototype.sort is stable: equal lines keep the wire's order.
    person.lines.sort(byLine);
    // Hours worked: a SPICE Travel line is a pay unit, printed in the file
    // and left out of every total: it is pay, not time worked.
    person.total = sumHours(person.lines.filter((l) => !l.travel).map((l) => l.hours));
    person.travelVisits = person.lines.filter((l) => l.travel).length;
  }
  return out.sort(
    (a, b) =>
      PAYROLL_COLLATOR.compare(a.name, b.name) || (a.staffUserId < b.staffUserId ? -1 : a.staffUserId > b.staffUserId ? 1 : 0),
  );
}

export function buildPayrollCsv(response: PayrollHoursResponse): string {
  const rows: string[][] = [[...PAYROLL_CSV_HEADER]];
  for (const person of payrollFilePeople(response)) {
    for (const l of person.lines) rows.push([l.name, l.date, l.hours, l.rate, l.clientOrInternal]);
  }
  return buildCsv(rows);
}

/** payroll-hours-YYYY-MM-DD_to_YYYY-MM-DD.csv: the period names the file, never a person. */
export function payrollCsvFilename(period: PayrollPeriodWire): string {
  return `payroll-hours-${period.start}_to_${period.end}.csv`;
}

// ---------------------------------------------------------------------
// The full detail file: every line of the period's hours ledger, one
// flat sheet, in wire order; the period's start and end ride on every
// row; blank cells stay blank. Its columns are what the Payroll Project
// reads, so they change only with it.
// ---------------------------------------------------------------------

export const PAYROLL_DETAIL_CSV_HEADER = [
  'staff_name',
  'staff_email',
  'rippling_employee_id',
  'employment_type',
  'billable_role',
  'work_date',
  'source',
  'kind',
  'client',
  'rate_band',
  'rate_label',
  'after_hours',
  'hours',
  'billing_ready',
  'pay_period_start',
  'pay_period_end',
  'ref',
  'derived_from',
  'in_period',
  'entered_at',
] as const;

function payrollDetailCsvRow(r: PayrollHoursRowWire, period: PayrollPeriodWire): string[] {
  return [
    r.staff_name,
    r.staff_email,
    r.rippling_employee_id,
    r.employment_type,
    r.billable_role,
    r.work_date,
    r.source,
    r.kind,
    r.client,
    r.rate_band,
    r.rate_label,
    String(r.after_hours),
    r.hours,
    String(r.billing_ready),
    period.start,
    period.end,
    r.ref,
    r.derived_from,
    String(r.in_period),
    r.entered_at,
  ];
}

export function buildPayrollDetailCsv(response: PayrollHoursResponse): string {
  return buildCsv([[...PAYROLL_DETAIL_CSV_HEADER], ...response.rows.map((r) => payrollDetailCsvRow(r, response.period))]);
}

/** payroll-hours-detail-YYYY-MM-DD_to_YYYY-MM-DD.csv. */
export function payrollDetailCsvFilename(period: PayrollPeriodWire): string {
  return `payroll-hours-detail-${period.start}_to_${period.end}.csv`;
}
